import { Alert } from './Alert';
import { SensorModelRegistry, SensorModelConfiguration } from '../features/sensor-configurations';

export interface SensorData {
  date: Date;
  temperature: number;
  humidity: number;
  [key: string]: any; // For other sensor values
}

export class Sensor {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly serialNumber: string,
    public readonly brand: string,
    public readonly model: string,
    public readonly locationId: string,
    public readonly locationName: string,
    public readonly organizationId: string,
    public readonly alerts: Record<string, Alert> = {},
    public readonly isActive: boolean = true,
    public readonly lastData: SensorData | null = null,
    public readonly last5Data: SensorData[] = [],
    public readonly createdAt: Date = new Date(),
    public readonly updatedAt: Date = new Date()
  ) {}

  static create(
    id: string,
    name: string,
    serialNumber: string,
    brand: string,
    model: string,
    locationId: string,
    locationName: string,
    organizationId: string
  ): Sensor {
    return new Sensor(
      id,
      name,
      serialNumber,
      brand,
      model,
      locationId,
      locationName,
      organizationId
    );
  }

  hasAlerts(): boolean {
    return Object.keys(this.alerts).length > 0;
  }

  getActiveAlerts(): Alert[] {
    return Object.values(this.alerts).filter(alert => alert.isActive());
  }

  /**
   * Récupère la configuration du modèle de capteur depuis le registre
   * @returns Configuration du modèle ou null si non configuré
   */
  getModelConfiguration(): SensorModelConfiguration | null {
    return SensorModelRegistry.getConfiguration(this.brand, this.model);
  }

  /**
   * Récupère les clés des métriques supportées par ce modèle
   * @returns Tableau des clés de métriques ou tableau vide si modèle non configuré
   */
  getSupportedMetrics(): string[] {
    return SensorModelRegistry.getMetricKeys(this.brand, this.model);
  }

  /**
   * Vérifie si une métrique est supportée par ce modèle de capteur
   * @param metricKey Clé de la métrique à vérifier
   * @returns true si la métrique est supportée, false sinon
   */
  supportsMetric(metricKey: string): boolean {
    const config = this.getModelConfiguration();
    return config ? metricKey in config.metrics : false;
  }

  /**
   * Vérifie si le modèle de ce capteur est configuré dans le registre
   * @returns true si le modèle est configuré, false sinon
   */
  isModelConfigured(): boolean {
    return SensorModelRegistry.isModelSupported(this.brand, this.model);
  }
}